import { Link } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { categories } from "@/mocks/categories";
import DramaCard from "@/components/feature/DramaCard";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import RankingSidebar from "@/pages/home/components/RankingSidebar";
import CategoryQuick from "@/pages/home/components/CategoryQuick";

export default function Home() {
  usePageMeta(
    "善良的秘书-热门电视剧资源与剧情介绍",
    "善良的秘书网站提供最新热门电视剧资源、剧情介绍以及演员资讯，覆盖多个热门影视类别，助力用户快速了解和追剧。"
  );

  const featured = dramas.filter((d) => d.featured);
  const latest = [...dramas].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, 8);
  const ranking = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 10);

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
          <div>
            <SectionHeading
              title="最新更新剧集"
              subtitle="按更新时间排序，第一时间掌握剧集动态"
              action={
                <Link
                  to="/category?sort=updated"
                  className="hidden sm:flex items-center gap-1 text-sm text-foreground-600 whitespace-nowrap cursor-pointer hover:text-primary-600 transition-colors"
                >
                  全部剧集
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              }
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
              {latest.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          </div>

          <aside className="space-y-6">
            <RankingSidebar items={ranking} />
            <CategoryQuick />
          </aside>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            title="剧集分类速览"
            subtitle="覆盖都市、古装、悬疑、科幻等多种热门类型"
            accent
            action={
              <Link
                to="/category"
                className="hidden sm:flex items-center gap-1 text-sm text-foreground-600 whitespace-nowrap cursor-pointer hover:text-primary-600 transition-colors"
              >
                进入分类页
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.slice(0, 5).map((c) => (
              <Link
                key={c.key}
                to={`/category?type=${encodeURIComponent(c.key)}`}
                className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="relative h-32 w-full overflow-hidden">
                  <img
                    src={c.image}
                    alt={`${c.name} 分类配图`}
                    title={`${c.name} 电视剧合集`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/80 to-transparent"></div>
                  <div className="absolute bottom-2 left-3 flex items-center gap-2 text-background-50">
                    <i className={`${c.icon} text-base text-accent-400`}></i>
                    <span className="font-heading text-sm font-bold">{c.name}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 p-3">
                  <p className="text-xs leading-relaxed text-foreground-600 line-clamp-2">{c.description}</p>
                  <span className="mt-1 font-label text-xs text-foreground-500">共 {c.count} 部作品</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              关于「善良的秘书」
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              我们是一个专注电视剧内容整理的门户平台。这里汇聚了热门剧集的资源索引、剧情梳理、主演名单与最新影视资讯，
              帮助追剧的你快速了解一部剧讲了什么、谁在演、值不值得看。无论你偏爱古装权谋还是都市现实，
              都能在分类筛选与热度榜单里找到下一部想看的作品。
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "收录剧集", value: "700+", icon: "ri-movie-2-line" },
                { label: "影视资讯", value: "1.2k", icon: "ri-newspaper-line" },
                { label: "演员档案", value: "3.6k", icon: "ri-user-star-line" },
                { label: "日均更新", value: "20", icon: "ri-refresh-line" },
              ].map((s) => (
                <div key={s.label} className="rounded-md bg-background-100 p-4">
                  <i className={`${s.icon} text-lg text-accent-600`}></i>
                  <p className="mt-2 font-label text-xl font-bold text-foreground-950">{s.value}</p>
                  <p className="text-xs text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-900 p-6 md:p-8 text-primary-100">
            <i className="ri-quill-pen-line text-2xl text-accent-400"></i>
            <h2 className="mt-3 font-heading text-lg font-bold text-background-50">影视资讯速递</h2>
            <p className="mt-3 text-sm leading-relaxed text-primary-200">
              新剧前瞻、深度剧评、行业观察，我们持续追踪荧屏内外的话题，帮你在开播前就做好功课。
            </p>
            <Link
              to="/news"
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
            >
              <i className="ri-article-line"></i>
              浏览全部资讯
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}